/**
 * Tripnovac Visa — User Dashboard Page
 * Matches production design: gradient welcome banner, KPI stat cards with
 * trend badges and sparklines, quick overview section, quick actions.
 *
 * @company Alpha Libra LLC
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  TrendingUp,
  Assignment,
  CheckCircle,
  Schedule,
  Warning,
  Add,
  Visibility,
  Download,
  Notifications,
  FlightTakeoff,
  DocumentScanner,
  Search,
  HomeOutlined,
  NavigateNext,
  Description,
  Public,
  FolderOpen,
  AttachMoney,
  SmartToy,
  Person,
  Groups,
  FactCheck,
  NotificationsActive,
  Language,
  ArrowForward,
  SupportAgent,
  VerifiedUser,
  AccessTime,
  Headphones,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardStats as ServiceDashboardStats } from '../../services/applicationService';
import { API_BASE_URL } from '../../constants';
import { readJsonResponse } from '../../lib/apiBaseUrl';

/* ── Types ──────────────────────────────────────────────────── */

interface DashboardStats extends ServiceDashboardStats {
  documentsUploaded: number;
  countriesVisited: number;
}

interface RecentApplication {
  id: string;
  destination: string;
  visaType: string;
  status: 'pending' | 'approved' | 'rejected' | 'in_review';
  submissionDate: string;
  expectedProcessingTime: string;
  progress: number;
  fee?: string;
  documents?: number;
  rejectionReason?: string;
}

interface DashboardNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: string;
  read: boolean;
  category?: string;
}

/* ── Sparkline mini-chart component ────────────────────────── */

const Sparkline: React.FC<{ data: number[]; color: string }> = ({ data, color }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const width = 120;
  const height = 28;
  const padding = 2;

  const points = data.map((v, i) => {
    const x = padding + (i / (data.length - 1)) * (width - padding * 2);
    const y = height - padding - ((v - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/* ── Stat Card matching production design ──────────────────── */

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  iconBg: string;
  trend?: string;
  trendColor?: string;
  sparkData?: number[];
  sparkColor?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title, value, icon, iconBg,
  trend, trendColor = '#29B848',
  sparkData = [2, 4, 3, 6, 5, 7, 6, 8],
  sparkColor = '#00BCEC',
}) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3,
      border: '1px solid rgba(255, 255, 255, 0.08)',
      bgcolor: 'rgba(15, 23, 42, 0.6)',
      height: '100%',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
    }}
  >
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      {/* Icon */}
      <Avatar
        sx={{
          width: 52,
          height: 52,
          background: iconBg,
          mb: 2,
          borderRadius: '14px',
        }}
      >
        {icon}
      </Avatar>

      {/* Value */}
      <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1, mb: 0.5 }}>
        {value}
      </Typography>

      {/* Trend Badge */}
      {trend && (
        <Chip
          label={trend}
          size="small"
          sx={{
            bgcolor: `${trendColor}18`,
            color: trendColor,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 22,
            mb: 0.5,
            '& .MuiChip-label': { px: 1 },
          }}
          icon={
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: trendColor,
                ml: 0.5,
                display: 'inline-block',
              }}
            />
          }
        />
      )}

      {/* Title */}
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
        {title}
      </Typography>

      {/* Sparkline */}
      <Sparkline data={sparkData} color={sparkColor} />
    </CardContent>
  </Card>
);

/* ── Quick Action Card ─────────────────────────────────────── */

const QuickActionCard: React.FC<{
  title: string;
  subtitle: string;
  gradient: string;
  icon: React.ReactNode;
  onClick: () => void;
}> = ({ title, subtitle, gradient, icon, onClick }) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      borderRadius: 3,
      background: gradient,
      color: 'white',
      cursor: 'pointer',
      height: '100%',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      },
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mb: 1.5 }}>
        {icon}
      </Avatar>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.85, fontSize: '0.8rem' }}>
        {subtitle}
      </Typography>
    </CardContent>
  </Card>
);

/* ── Main Dashboard Component ──────────────────────────────── */

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stats, setStats] = useState<DashboardStats>({
    totalApplications: 0,
    pendingApplications: 0,
    approvedApplications: 0,
    rejectedApplications: 0,
    documentsUploaded: 0,
    countriesVisited: 0,
    recentApplications: [],
  });
  const [recentApplications, setRecentApplications] = useState<RecentApplication[]>([]);
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentStats, setPaymentStats] = useState<{ totalPaid: number; pending: number }>({ totalPaid: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  /* ── Data Fetch ──────────────────────────────────────────── */

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('tripnovac_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Fetch dashboard stats from API
      const [dashRes, appsRes, notifsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/individual`, { headers }).catch(() => null),
        fetch(`${API_BASE_URL}/applications/my-applications`, { headers }).catch(() => null),
        fetch(`${API_BASE_URL}/notifications`, { headers }).catch(() => null),
      ]);

      if (dashRes?.ok) {
        const dashData = await readJsonResponse<any>(dashRes);
        const d = dashData.data || dashData;
        setStats({
          totalApplications: d.totalApplications || 0,
          pendingApplications: d.pendingApplications || 0,
          approvedApplications: d.approvedApplications || 0,
          rejectedApplications: d.rejectedApplications || 0,
          documentsUploaded: d.documentsUploaded || 0,
          countriesVisited: d.countriesAvailable || d.countriesVisited || 0,
          recentApplications: d.recentApplications || [],
        });
      }

      if (appsRes?.ok) {
        const appsData = await readJsonResponse<any>(appsRes);
        const apps = Array.isArray(appsData.data)
          ? appsData.data
          : Array.isArray(appsData.applications)
            ? appsData.applications
            : [];
        setRecentApplications(apps.slice(0, 5).map((a: any) => ({
          id: a.tracking_number || a.trackingNumber || a.id,
          destination: a.destination_country || a.destinationCountry || a.destination || '',
          visaType: a.visa_type || a.visaType || '',
          status: (a.status || 'pending').toLowerCase().replace(' ', '_'),
          submissionDate: a.created_at || a.createdAt || a.submissionDate || '',
          expectedProcessingTime: a.processing_time || a.processingTime || '',
          progress: a.progress || (a.status === 'approved' ? 100 : a.status === 'rejected' ? 100 : a.status === 'in_review' ? 65 : 25),
          fee: a.fee ? `$${a.fee}` : '',
          documents: a.documents_count || a.documentsCount || 0,
        })));
      }

      if (notifsRes?.ok) {
        const notifsData = await readJsonResponse<any>(notifsRes);
        const notifs = Array.isArray(notifsData.data)
          ? notifsData.data
          : Array.isArray(notifsData.notifications)
            ? notifsData.notifications
            : [];
        setNotifications(notifs.slice(0, 5).map((n: any) => ({
          id: n.id,
          title: n.title || '',
          message: n.message || n.body || '',
          type: n.type || 'info',
          timestamp: n.created_at || n.createdAt || n.timestamp || '',
          read: n.read || n.is_read || false,
        })));
      }

      // Fetch payment stats
      try {
        const paymentsRes = await fetch(`${API_BASE_URL}/payments`, { headers });
        if (paymentsRes?.ok) {
          const paymentsData = await readJsonResponse<any>(paymentsRes);
          const paymentsList = Array.isArray(paymentsData.data)
            ? paymentsData.data
            : Array.isArray(paymentsData.payments)
              ? paymentsData.payments
              : [];
          const totalPaid = paymentsList
            .filter((p: any) => p.status === 'completed' || p.status === 'paid')
            .reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
          const pending = paymentsList
            .filter((p: any) => p.status === 'pending')
            .reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
          setPaymentStats({ totalPaid, pending });
        }
      } catch {
        // Payment data unavailable
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const userRole = (user?.role || 'general').toLowerCase() === 'user' ? 'general' : (user?.role || 'general').toLowerCase();

  /* ── Helpers ─────────────────────────────────────────────── */

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      case 'in_review': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle />;
      case 'rejected': return <Warning />;
      case 'pending': return <Schedule />;
      case 'in_review': return <Assignment />;
      default: return <Schedule />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  const firstName = user?.firstName || user?.fullName?.split(' ')[0] || 'User';

  const getRoleLabel = () => {
    switch (userRole) {
      case 'admin': return 'Admin Account';
      case 'enterprise': return 'Enterprise Account';
      case 'corporate': return 'Corporate Account';
      case 'agent': return 'Agent Account';
      default: return 'General Account';
    }
  };

  /* ── Render ──────────────────────────────────────────────── */

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          color="text.secondary"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}
        >
          <HomeOutlined sx={{ fontSize: 18 }} /> Home
        </Link>
        <Typography color="text.primary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
          Dashboard
        </Typography>
      </Breadcrumbs>

      {/* ── Welcome Banner ──────────────────────────────────── */}
      <Box
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4,
          minHeight: 200,
          backgroundImage: 'url("https://images.unsplash.com/photo-1488085061387-422e29b40080?w=1920&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 3, md: 5 },
          py: 4,
          pb: 7,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'rgba(255,255,255,0.2)',
              border: '2px solid rgba(255,255,255,0.3)',
            }}
          >
            <FlightTakeoff sx={{ color: 'white', fontSize: 24 }} />
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                Welcome back, {firstName}!
              </Typography>
              <Chip
                label={getRoleLabel()}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 26,
                  backdropFilter: 'blur(4px)',
                }}
              />
            </Box>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
              Your personal visa applications dashboard
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Quick Overview Header ───────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Quick Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track your applications and manage documents
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/applications/new')}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #1273E5 0%, #00BCEC 100%)',
          }}
        >
          New Application
        </Button>
      </Box>

      {/* ── KPI Stat Cards ──────────────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Applications"
            value={stats.totalApplications}
            icon={<Description sx={{ color: 'white', fontSize: 24 }} />}
            iconBg="linear-gradient(135deg, #7C4DFF 0%, #651FFF 100%)"
            trend={`+${Math.max(1, Math.round(stats.totalApplications * 0.12))}%`}
            trendColor="#29B848"
            sparkData={[3, 5, 4, 7, 6, 9, 8, 12]}
            sparkColor="#7C4DFF"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Approved"
            value={stats.approvedApplications}
            icon={<CheckCircle sx={{ color: 'white', fontSize: 24 }} />}
            iconBg="linear-gradient(135deg, #29B848 0%, #43A047 100%)"
            trend="+8%"
            trendColor="#29B848"
            sparkData={[1, 3, 2, 5, 4, 3, 5, 4]}
            sparkColor="#29B848"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="In Progress"
            value={stats.pendingApplications}
            icon={<Schedule sx={{ color: 'white', fontSize: 24 }} />}
            iconBg="linear-gradient(135deg, #E91E63 0%, #EC407A 100%)"
            sparkData={[4, 3, 5, 2, 6, 4, 3, 5]}
            sparkColor="#E91E63"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Countries"
            value={stats.countriesVisited}
            icon={<Public sx={{ color: 'white', fontSize: 24 }} />}
            iconBg="linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)"
            trend={`+${Math.max(1, stats.countriesVisited)}`}
            trendColor="#29B848"
            sparkData={[0, 0, 1, 1, 1, 1, 1, 1]}
            sparkColor="#00BCD4"
          />
        </Grid>
      </Grid>

      {/* ── Quick Actions ───────────────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Start Application"
            subtitle="Apply for a new visa"
            gradient="linear-gradient(135deg, #29B848 0%, #43A047 100%)"
            icon={<Add sx={{ color: 'white' }} />}
            onClick={() => navigate('/applications/new')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Visa Requirements"
            subtitle="Check what you need"
            gradient="linear-gradient(135deg, #1273E5 0%, #00BCEC 100%)"
            icon={<Search sx={{ color: 'white' }} />}
            onClick={() => navigate('/visa-check')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Track Applications"
            subtitle="View application status"
            gradient="linear-gradient(135deg, #26A69A 0%, #00897B 100%)"
            icon={<Assignment sx={{ color: 'white' }} />}
            onClick={() => navigate('/applications')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="My Documents"
            subtitle="Upload & manage docs"
            gradient="linear-gradient(135deg, #7C4DFF 0%, #651FFF 100%)"
            icon={<FolderOpen sx={{ color: 'white' }} />}
            onClick={() => navigate('/documents')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Travel Intelligence"
            subtitle="Country info & alerts"
            gradient="linear-gradient(135deg, #29B848 0%, #66BB6A 100%)"
            icon={<FlightTakeoff sx={{ color: 'white' }} />}
            onClick={() => navigate('/countries')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Buy Credits"
            subtitle="Get more application credits"
            gradient="linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)"
            icon={<AttachMoney sx={{ color: 'white' }} />}
            onClick={() => navigate('/credits/purchase')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="AI Visa Advisor"
            subtitle="Get personalized help"
            gradient="linear-gradient(135deg, #FF7043 0%, #F4511E 100%)"
            icon={<SmartToy sx={{ color: 'white' }} />}
            onClick={() => navigate('/ai-advisor')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="My Profile"
            subtitle="Update personal info"
            gradient="linear-gradient(135deg, #78909C 0%, #546E7A 100%)"
            icon={<Person sx={{ color: 'white' }} />}
            onClick={() => navigate('/profile')}
          />
        </Grid>
      </Grid>

      {/* ── Visa Processing Hub ──────────────────────────────── */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Chip label="🔴 NEW FEATURES" size="small" sx={{ bgcolor: 'rgba(230, 81, 0, 0.15)', color: '#FF9800', fontWeight: 700, mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Visa Processing Hub
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Everything you need to succeed in your visa application
        </Typography>
      </Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', background: 'linear-gradient(135deg, #7C4DFF 0%, #B388FF 100%)', color: 'white', textAlign: 'center', py: 3, px: 2 }}>
            <CardContent>
              <Groups sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Interview Prep</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mb: 2, fontSize: '0.8rem' }}>Practice with 100+ real interview questions</Typography>
              <Chip label="Mock Interviews" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }} onClick={() => navigate('/interview-prep/US')} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', background: 'linear-gradient(135deg, #29B848 0%, #66BB6A 100%)', color: 'white', textAlign: 'center', py: 3, px: 2 }}>
            <CardContent>
              <FactCheck sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Doc Checklist</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mb: 2, fontSize: '0.8rem' }}>Track all required documents & uploads</Typography>
              <Chip label="Never Miss a Doc" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }} onClick={() => navigate('/documents')} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', background: 'linear-gradient(135deg, #FF7043 0%, #FFB74D 100%)', color: 'white', textAlign: 'center', py: 3, px: 2 }}>
            <CardContent>
              <NotificationsActive sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Reminders</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mb: 2, fontSize: '0.8rem' }}>Deadlines, interviews & appointments</Typography>
              <Chip label="Stay On Track" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }} onClick={() => navigate('/reminders')} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', background: 'linear-gradient(135deg, #00BCD4 0%, #FF7043 100%)', color: 'white', textAlign: 'center', py: 3, px: 2 }}>
            <CardContent>
              <Language sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Country Hub</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mb: 2, fontSize: '0.8rem' }}>Requirements, tips & embassy info</Typography>
              <Chip label="249 countries" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }} onClick={() => navigate('/countries')} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Preparation Banner ───────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(255, 255, 255, 0.08)', bgcolor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(10px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TrendingUp sx={{ fontSize: 32, color: '#29B848' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#29B848' }}>Prepare Before You Submit</Typography>
            <Typography variant="body2" color="text.secondary">Review current authority requirements, documents, fees, and interview topics</Typography>
          </Box>
        </Box>
        <Button variant="contained" onClick={() => navigate('/visa-check')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #29B848 0%, #43A047 100%)' }}>
          Start Preparing Now
        </Button>
      </Paper>

      {/* ── Recent Applications + Notifications ─────────────── */}
      <Grid container spacing={4}>
        {/* Recent Applications */}
        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(255, 255, 255, 0.08)', bgcolor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(10px)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Recent Applications
              </Typography>
              <Button
                startIcon={<Add />}
                variant="outlined"
                size="small"
                onClick={() => navigate('/applications')}
              >
                View All
              </Button>
            </Box>

            {recentApplications.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Destination</TableCell>
                      <TableCell>Visa Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentApplications.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            {app.destination}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Applied {new Date(app.submissionDate).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{app.visaType}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(app.status)}
                            label={app.status.replace('_', ' ').toUpperCase()}
                            color={getStatusColor(app.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={app.progress}
                              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption">{app.progress}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => navigate(`/applications/${app.id}`)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                          <IconButton size="small">
                            <Download fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DocumentScanner sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No applications yet
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/visa-check')}>
                  Start Application
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Notifications */}
        <Grid item xs={12} lg={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(255, 255, 255, 0.08)', bgcolor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(10px)', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Notifications sx={{ fontSize: 20 }} /> Notifications
            </Typography>

            {notifications.length > 0 ? (
              <List dense>
                {notifications.map((n, i) => (
                  <React.Fragment key={n.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: `${n.type}.main`,
                            color: 'white',
                          }}
                        >
                          <Notifications sx={{ fontSize: 14 }} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={n.title}
                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', display: 'block' }}>
                              {n.message}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary">
                              {new Date(n.timestamp).toLocaleDateString()}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {i < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No new notifications
              </Typography>
            )}
          </Paper>

          {/* Need Help? Sidebar */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)', color: 'white' }}>
            <Box sx={{ textAlign: 'center' }}>
              <SupportAgent sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Need Help?</Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>Submit a support request and our team will respond during available coverage</Typography>
              <Button variant="contained" fullWidth onClick={() => navigate('/help')} sx={{ bgcolor: 'white', color: '#0097A7', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}>
                Contact Support
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ── 3-Step Process ───────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid rgba(255, 255, 255, 0.08)', bgcolor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(10px)', mb: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Get your visa in 3 simple steps</Typography>
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <Grid item xs={12} sm={3}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(18, 115, 229, 0.15)', mx: 'auto', mb: 1 }}><Search sx={{ fontSize: 32, color: '#42A5F5' }} /></Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Check Requirements</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Use our visa checker to instantly see what documents you need. No guesswork.</Typography>
          </Grid>
          <Grid item xs={12} sm={1} sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center' }}>
            <ArrowForward sx={{ fontSize: 32, color: '#29B848' }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(41, 184, 72, 0.15)', mx: 'auto', mb: 1 }}><Description sx={{ fontSize: 32, color: '#66BB6A' }} /></Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Apply Online</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Fill out our smart application form. Auto-save keeps your progress safe.</Typography>
          </Grid>
          <Grid item xs={12} sm={1} sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center' }}>
            <ArrowForward sx={{ fontSize: 32, color: '#29B848' }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(0, 188, 212, 0.15)', mx: 'auto', mb: 1 }}><VerifiedUser sx={{ fontSize: 32, color: '#4DD0E1' }} /></Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Receive Your Visa</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>We handle the rest. Track status in real-time and get your visa delivered.</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Why Travelers Choose Tripnovac ────────────────────── */}
      <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center', mb: 3 }}>Why Travelers Choose Tripnovac</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { value: '249', label: 'Countries and territories', sub: 'ISO 3166-1 English short names', icon: <Public sx={{ fontSize: 40, color: '#1273E5' }} /> },
          { value: 'Account', label: 'Tracked Records', sub: 'Application workflow history', icon: <CheckCircle sx={{ fontSize: 40, color: '#29B848' }} /> },
          { value: 'Ticket', label: 'Support Requests', sub: 'Persistent support workflow', icon: <Headphones sx={{ fontSize: 40, color: '#7C4DFF' }} /> },
          { value: 'Varies', label: 'Processing Time', sub: 'Set by the relevant authority', icon: <AccessTime sx={{ fontSize: 40, color: '#FF7043' }} /> },
        ].map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(255, 255, 255, 0.08)', bgcolor: 'rgba(15, 23, 42, 0.6)', textAlign: 'center', height: '100%' }}>
              {stat.icon}
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mt: 1 }}>{stat.value}</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{stat.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{stat.sub}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Popular Destinations ──────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Popular Destinations</Typography>
          <Typography variant="body2" color="text.secondary">Most requested visa applications</Typography>
        </Box>
        <Button variant="outlined" size="small" endIcon={<ArrowForward />} onClick={() => navigate('/countries')} sx={{ borderRadius: 2, textTransform: 'none' }}>
          View All Countries
        </Button>
      </Box>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { code: 'GB', name: 'United Kingdom' },
          { code: 'US', name: 'United States' },
          { code: 'CA', name: 'Canada' },
          { code: 'AU', name: 'Australia' },
          { code: 'EU', name: 'Schengen' },
          { code: 'JP', name: 'Japan' },
        ].map((dest) => (
          <Grid item xs={6} sm={4} md={2} key={dest.code}>
            <Card
              elevation={0}
              onClick={() => navigate(`/countries/${dest.code}`)}
              sx={{ borderRadius: 3, border: '1px solid rgba(255, 255, 255, 0.08)', bgcolor: 'rgba(15, 23, 42, 0.6)', textAlign: 'center', p: 2, cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' } }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.secondary' }}>{dest.code}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{dest.name}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardPage;
